import React, { useState, useMemo } from 'react';
import { useAppContext } from '../contexts/AppContext';
import { Customer } from '../types';
import { Building2, Plus, Edit2, Trash2, Globe, Users, Save } from 'lucide-react';
import { ModuleInfoModal } from '../components/ModuleInfoModal';
import { ModalWrapper } from '../components/ModalWrapper';

export default function CustomerManagement() {
    const { allData, activeProject, updateCustomer, deleteCustomer } = useAppContext();
    const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
    const [showCustomerModal, setShowCustomerModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterTier, setFilterTier] = useState<string>('all');
    const [filterIndustry, setFilterIndustry] = useState<string>('all');

    // Filter customers by active project
    const projectCustomers = useMemo(() => {
        if (!allData.customers) return [];
        if (activeProject === 'all') return allData.customers;
        return allData.customers.filter(c => c.projectName === activeProject);
    }, [allData.customers, activeProject]);

    // Apply search and filters
    const filteredCustomers = useMemo(() => {
        let filtered = projectCustomers;

        if (searchTerm) {
            filtered = filtered.filter(c =>
                c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                c.industry?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                c.countryId?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        if (filterTier !== 'all') {
            filtered = filtered.filter(c => c.tier === filterTier);
        }

        if (filterIndustry !== 'all') {
            filtered = filtered.filter(c => c.industry === filterIndustry);
        }

        return filtered;
    }, [projectCustomers, searchTerm, filterTier, filterIndustry]);

    // Get unique industries
    const industries = useMemo(() => {
        const uniqueIndustries = new Set(projectCustomers.map(c => c.industry).filter(Boolean));
        return Array.from(uniqueIndustries).sort();
    }, [projectCustomers]);

    const createNewCustomer = () => {
        if (activeProject === 'all') {
            alert('Please select a specific project first');
            return;
        }
        setSelectedCustomer({
            id: `cust-${Date.now()}`,
            name: '',
            countryId: 'USA',
            industry: '',
            tier: 'OEM',
            projectName: activeProject,
            epochId: allData.projectMetadata?.epochId || 0
        });
        setIsEditing(false);
        setShowCustomerModal(true);
    };

    const editCustomer = (customer: Customer) => {
        setSelectedCustomer({ ...customer });
        setIsEditing(true);
        setShowCustomerModal(true);
    };

    const saveCustomer = async (customer: Customer) => {
        await updateCustomer(customer);
        setShowCustomerModal(false);
        setSelectedCustomer(null);
    };

    const handleDeleteCustomer = async (customerId: string) => {
        if (confirm('Delete this customer? This cannot be undone.')) {
            await deleteCustomer(customerId);
        }
    };

    const getTierColor = (tier: string) => {
        const colors: Record<string, string> = {
            'OEM': 'bg-purple-100 text-purple-700 border-purple-300',
            'Tier 1': 'bg-blue-100 text-blue-700 border-blue-300',
            'Tier 2': 'bg-green-100 text-green-700 border-green-300',
            'Tier 3': 'bg-yellow-100 text-yellow-700 border-yellow-300',
            'End User': 'bg-gray-100 text-gray-700 border-gray-300'
        };
        return colors[tier] || colors['OEM'];
    };

    // Get stats
    const stats = useMemo(() => {
        const tierCounts: Record<string, number> = {};
        projectCustomers.forEach(c => {
            tierCounts[c.tier] = (tierCounts[c.tier] || 0) + 1;
        });
        return {
            total: projectCustomers.length,
            oem: tierCounts['OEM'] || 0,
            tier1: tierCounts['Tier 1'] || 0,
            tier2: tierCounts['Tier 2'] || 0
        };
    }, [projectCustomers]);

    // Get linked requirements for customer
    const getLinkedRequirements = (customerId: string) => {
        return allData.customerRequirements?.filter(r =>
            r.id.includes(customerId) || r.projectName === activeProject
        ) || [];
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-card rounded-lg shadow-md p-6 border border-border">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h1 className="text-3xl font-bold text-text-primary flex items-center gap-3">
                            <Building2 className="w-8 h-8 text-primary" />
                            Customer Management
                        </h1>
                        <p className="text-text-secondary mt-2">
                            Manage customer organizations for {activeProject === 'all' ? 'all projects' : activeProject}
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <ModuleInfoModal
                            title="Customer Management"
                            description="Manage your customer portfolio, including OEMs, suppliers, and end users across different industries and tiers."
                            benefits={[
                                "Centralized customer database",
                                "Industry and tier classification",
                                "Customer-to-requirement traceability"
                            ]}
                            usageInstructions={[
                                "Click 'New Customer' to add a customer",
                                "Filter by industry or tier",
                                "Link customers to requirements and projects"
                            ]}
                        />
                        <button
                            onClick={createNewCustomer}
                            disabled={activeProject === 'all'}
                            className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-secondary text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Plus className="w-4 h-4" />
                            New Customer
                        </button>
                    </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-4 gap-4 mt-4">
                    <div className="bg-background p-4 rounded-lg border border-border">
                        <div className="text-sm text-text-secondary">Total Customers</div>
                        <div className="text-2xl font-bold text-text-primary">{stats.total}</div>
                    </div>
                    <div className="bg-background p-4 rounded-lg border border-border">
                        <div className="text-sm text-text-secondary">OEMs</div>
                        <div className="text-2xl font-bold text-purple-600">{stats.oem}</div>
                    </div>
                    <div className="bg-background p-4 rounded-lg border border-border">
                        <div className="text-sm text-text-secondary">Tier 1</div>
                        <div className="text-2xl font-bold text-blue-600">{stats.tier1}</div>
                    </div>
                    <div className="bg-background p-4 rounded-lg border border-border">
                        <div className="text-sm text-text-secondary">Tier 2</div>
                        <div className="text-2xl font-bold text-green-600">{stats.tier2}</div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-card rounded-lg shadow-md p-4 border border-border">
                <div className="grid grid-cols-3 gap-4">
                    <div>
                        <label className="text-sm text-text-secondary mb-2 block">Search</label>
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search customers..."
                            className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary outline-none"
                        />
                    </div>
                    <div>
                        <label className="text-sm text-text-secondary mb-2 block">Tier</label>
                        <select
                            value={filterTier}
                            onChange={(e) => setFilterTier(e.target.value)}
                            className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary outline-none"
                        >
                            <option value="all">All Tiers</option>
                            <option value="OEM">OEM</option>
                            <option value="Tier 1">Tier 1</option>
                            <option value="Tier 2">Tier 2</option>
                            <option value="Tier 3">Tier 3</option>
                            <option value="End User">End User</option>
                        </select>
                    </div>
                    <div>
                        <label className="text-sm text-text-secondary mb-2 block">Industry</label>
                        <select
                            value={filterIndustry}
                            onChange={(e) => setFilterIndustry(e.target.value)}
                            className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary outline-none"
                        >
                            <option value="all">All Industries</option>
                            {industries.map(industry => (
                                <option key={industry} value={industry}>{industry}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {/* Customers Table */}
            <div className="bg-card rounded-lg shadow-md border border-border overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-background">
                            <tr>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">Customer</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">Industry</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">Tier</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">Country</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">Requirements</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {filteredCustomers.map(customer => {
                                const linkedReqs = getLinkedRequirements(customer.id);

                                return (
                                    <tr key={customer.id} className="hover:bg-background transition-colors">
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                <Building2 className="w-5 h-5 text-primary" />
                                                <div>
                                                    <div className="font-medium text-text-primary">{customer.name}</div>
                                                    <div className="text-xs text-text-secondary font-mono">{customer.id}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-text-secondary">{customer.industry}</td>
                                        <td className="px-4 py-3">
                                            <span className={`px-2 py-1 rounded text-xs font-semibold border ${getTierColor(customer.tier)}`}>
                                                {customer.tier}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-1 text-sm text-text-secondary">
                                                <Globe className="w-3 h-3" />
                                                {customer.countryId}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-1 text-sm text-text-secondary">
                                                <Users className="w-3 h-3" />
                                                {linkedReqs.length} Requirements
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => editCustomer(customer)}
                                                    className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                                    title="Edit"
                                                >
                                                    <Edit2 className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteCustomer(customer.id)}
                                                    className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                                                    title="Delete"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {filteredCustomers.length === 0 && (
                    <div className="p-12 text-center">
                        <Building2 className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                        <p className="text-lg font-medium text-text-primary mb-2">No Customers Found</p>
                        <p className="text-sm text-text-secondary">
                            {searchTerm || filterTier !== 'all' || filterIndustry !== 'all'
                                ? 'Try adjusting your filters'
                                : activeProject === 'all'
                                    ? 'Select a project to manage customers'
                                    : 'Click "New Customer" to add your first customer'}
                        </p>
                    </div>
                )}
            </div>

            {/* Customer Modal */}
            {showCustomerModal && selectedCustomer && (
                <CustomerModal
                    customer={selectedCustomer}
                    isEditing={isEditing}
                    onSave={saveCustomer}
                    onCancel={() => {
                        setShowCustomerModal(false);
                        setSelectedCustomer(null);
                    }}
                />
            )}
        </div>
    );
}

// Customer Modal Component
function CustomerModal({
    customer,
    isEditing,
    onSave,
    onCancel
}: {
    customer: Customer;
    isEditing: boolean;
    onSave: (customer: Customer) => void;
    onCancel: () => void;
}) {
    const [formData, setFormData] = useState(customer);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSave(formData);
    };

    return (
        <ModalWrapper
            isOpen={true}
            onClose={onCancel}
            title={isEditing ? 'Edit Customer' : 'New Customer'}
        >
            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">Customer Name *</label>
                    <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-4 py-2 bg-background border border-border rounded focus:ring-2 focus:ring-primary outline-none"
                        placeholder="e.g., HomeStyle Kitchen Co."
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">Industry *</label>
                    <input
                        type="text"
                        required
                        value={formData.industry}
                        onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                        className="w-full px-4 py-2 bg-background border border-border rounded focus:ring-2 focus:ring-primary outline-none"
                        placeholder="e.g., Kitchen Appliances, Automotive, Aerospace"
                    />
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-text-secondary mb-2">Tier *</label>
                        <select
                            required
                            value={formData.tier}
                            onChange={(e) => setFormData({ ...formData, tier: e.target.value as any })}
                            className="w-full px-4 py-2 bg-background border border-border rounded focus:ring-2 focus:ring-primary outline-none"
                        >
                            <option value="OEM">OEM</option>
                            <option value="Tier 1">Tier 1 Supplier</option>
                            <option value="Tier 2">Tier 2 Supplier</option>
                            <option value="Tier 3">Tier 3 Supplier</option>
                            <option value="End User">End User</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-text-secondary mb-2">Country *</label>
                        <input
                            type="text"
                            required
                            value={formData.countryId}
                            onChange={(e) => setFormData({ ...formData, countryId: e.target.value })}
                            className="w-full px-4 py-2 bg-background border border-border rounded focus:ring-2 focus:ring-primary outline-none"
                            placeholder="e.g., USA, Germany, Japan"
                        />
                    </div>
                </div>

                <div className="flex gap-3 justify-end mt-6 pt-4 border-t border-border">
                    <button
                        type="button"
                        onClick={onCancel}
                        className="px-4 py-2 text-sm font-medium text-text-secondary hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="px-4 py-2 text-sm font-bold text-white bg-primary hover:bg-primary-dark rounded-lg shadow-lg"
                    >
                        <Save size={16} className="inline mr-2" />
                        {isEditing ? 'Save Changes' : 'Create Customer'}
                    </button>
                </div>
            </form>
        </ModalWrapper>
    );
}
